"use client";

import { useState, useEffect, useRef, useMemo, useId } from "react";
import dynamic from "next/dynamic";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useCountries } from "@/contexts/CountriesContext";
import AgreementModal from "./AgreementModal";
import DatePickerCustom from "./DatePickerCustom";
import type { Room } from "@/types/travelers";
import { format, parseISO, startOfDay } from "date-fns";
import {
    loadRecaptchaScript,
    renderRecaptchaWidget,
    resetRecaptchaWidget,
    getRecaptchaToken,
    loadRecaptchaV3Script,
    getRecaptchaV3Token,
    loadTurnstileScript,
    renderTurnstileWidget,
    resetTurnstileWidget,
    getTurnstileToken,
    removeTurnstileWidget,
} from "@/lib/captcha";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import { useFormSubmitReadiness } from "@/hooks/useFormSubmitReadiness";
import { validateDynamicFormFields } from "@/utils/form-fields-validation";
import RecaptchaLegalNotice from "@/components/ui/RecaptchaLegalNotice";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const ReactSelect = dynamic(() => import("react-select"), { ssr: false });
import { components } from "react-select";
import type { InputProps, GroupBase } from "react-select";

/** Tarayıcı otomatik doldurmasını kapatır; uygulama dropdown'ının üstte kalması için. */
const NoAutocompleteInput = (props: InputProps<unknown, false, GroupBase<unknown>>) => {
    const uniqueId = useId();
    const { onFocus, readOnly: _ro, ...rest } = props;
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.removeAttribute("readonly");
        onFocus?.(e);
    };
    return (
        <components.Input
            {...rest}
            onFocus={handleFocus}
            autoComplete="nope"
            aria-autocomplete="list"
            data-lpignore="true"
            data-form-type="other"
            name={`country-residence-${uniqueId.replace(/:/g, "")}`}
            readOnly
        />
    );
};

interface AgreementItem {
    id: number;
    title: string;
    slug: string;
    content?: string;
    is_required: boolean;
}

interface FormAgreements {
    required: AgreementItem[];
    optional: AgreementItem[];
}

interface FormField {
    type?: string;
    name?: string;
    field?: string;
    label?: string;
    title?: string;
    placeholder?: string;
    required?: boolean | string | number;
    rows?: number;
    /** Select/dropdown seçenekleri: string[] veya { value, label }[] */
    options?: string[] | Array<{ value: string; label: string }>;
}

interface FormDetails {
    id: number;
    title: string;
    slug: string;
    language?: { id?: number; name?: string; code?: string };
    agreements: FormAgreements;
    fields?: FormField[];
}

/** Talep formu checkout akışı: cookie ile checkout sayfasına yönlendir, kişi bilgileri orada alınsın */
export interface RequestCheckoutSummary {
    flow: 'request_first';
    form_id: number;
    product_id: number;
    product_type: string;
    product_name?: string;
    travel_date?: string;
    check_in_date?: string;
    check_out_date?: string;
    rooms?: Array<{ adults: number; children: number; childAges?: number[] }>;
    guest_count: number;
    child_count: number;
    form_values: Record<string, unknown>;
    agreements: Array<{ id: number; accepted: boolean; required?: boolean }>;
    language_id?: number;
}

interface RequestFormProps {
    productId: number;
    productType: string;
    productName?: string;
    language?: string;
    languageId?: number;
    translations?: Record<string, string>;
    className?: string;
    /** Sayfa render sırasında çekilmiş form detayı; verilirse ek istek atılmaz */
    initialFormDetails?: FormDetails | null;
    /** true ise form submit yerine checkout'a yönlendir (kişi bilgileri checkout'ta alınır) */
    redirectToCheckout?: boolean;
    /** redirectToCheckout=true iken submit'te çağrılır; parent cookie kaydedip yönlendirir */
    onProceedToCheckout?: (summary: RequestCheckoutSummary) => void;
}

interface CountryOption {
    id: number;
    code?: string;
    name: string;
}

const labelClass = "font-semibold text-sm text-alsero-dark-gray mb-1.5 block";
const labelClassSmall = "font-semibold text-xs text-alsero-dark-gray mb-1.5 block";
const inputClass =
    "w-full border border-[rgba(0,0,0,0.08)] focus:border-alsero-first/40 focus:outline-none focus:ring-1 focus:ring-alsero-first/20 rounded-lg px-4 py-3 bg-white text-alsero-dark-gray text-sm transition-colors placeholder:text-gray-400";

const searchableSelectStyles = {
    control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
        ...base,
        borderColor: state.isFocused ? "rgba(227, 30, 36, 0.4)" : "rgba(0,0,0,0.08)",
        borderRadius: "0.5rem",
        minHeight: "48px",
        boxShadow: "none",
        fontSize: "0.875rem",
        "&:hover": { borderColor: "rgba(0,0,0,0.15)" },
    }),
    option: (base: Record<string, unknown>, state: { isSelected?: boolean; isFocused?: boolean }) => ({
        ...base,
        backgroundColor: state.isSelected ? "#E31E24" : state.isFocused ? "#fef2f2" : "white",
        color: state.isSelected ? "white" : "#4F545A",
        fontSize: "0.875rem",
        padding: "10px 16px",
    }),
    placeholder: (base: Record<string, unknown>) => ({ ...base, color: "#9CA3AF" }),
    singleValue: (base: Record<string, unknown>) => ({ ...base, color: "#4F545A" }),
    menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: "none" as const }),
};

export default function RequestForm({
    productId,
    productType,
    productName,
    language = "tr",
    languageId,
    translations = {},
    className = "",
    initialFormDetails = null,
    redirectToCheckout = false,
    onProceedToCheckout,
}: RequestFormProps) {
    const { getVariable } = useLanguageVariables();
    const t = (key: string, fallback = "") => translations[key] || getVariable(key, fallback);

    const [formDetails, setFormDetails] = useState<FormDetails | null>(() => {
        const n = Array.isArray(initialFormDetails?.fields) ? initialFormDetails.fields.length : 0;
        return n > 0 ? initialFormDetails : null;
    });
    const [formDetailsLoading, setFormDetailsLoading] = useState(() => {
        if (!initialFormDetails) return true;
        const n = Array.isArray(initialFormDetails.fields) ? initialFormDetails.fields.length : 0;
        return n === 0;
    });
    const [formDetailsError, setFormDetailsError] = useState<string | null>(null);
    const countries = useCountries();
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>({});
    const [selectedAgreement, setSelectedAgreement] = useState<AgreementItem | null>(null);
    const [agreementModalOpen, setAgreementModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const captchaWidgetIdRef = useRef<number | string | null>(null);
    const { config: captchaConfig, isLoading: captchaConfigLoading } = useCaptchaConfig();

    /** Tur/ship_tour talep formunda people yerine oda bazlı seçim (max 40 oda) */
    const supportsRoomSelector = productType === "tour" || productType === "ship_tour";
    const [requestFormRooms, setRequestFormRooms] = useState<Room[]>([{ adults: 2, children: 0, childAges: [] }]);

    // Form detayını product_id + product_type ile al (initialFormDetails yoksa)
    // initialFormDetails?.id dependency; state sadece form id değişince güncellenir (sonsuz döngü önlenir)
    useEffect(() => {
        const initialFieldCount = Array.isArray(initialFormDetails?.fields) ? initialFormDetails.fields.length : 0;
        if (initialFormDetails && initialFieldCount > 0) {
            setFormDetails((prev) => (prev?.id === initialFormDetails?.id ? prev : initialFormDetails));
            setFormDetailsLoading(false);
            setFormDetailsError(null);
            const nextAgreements: Record<number, boolean> = {};
            (initialFormDetails?.agreements?.required || []).forEach((a) => { nextAgreements[a.id] = true; });
            (initialFormDetails?.agreements?.optional || []).forEach((a) => { nextAgreements[a.id] = false; });
            setAgreementsState(nextAgreements);
            return;
        }
        const params = new URLSearchParams();
        params.set("product_id", String(productId));
        params.set("product_type", productType);
        if (languageId) params.set("language_id", String(languageId));

        const controller = new AbortController();
        loadingTimeoutRef.current = setTimeout(() => {
            if (!controller.signal.aborted) setFormDetailsLoading(true);
        }, 200);

        fetch(`/api/forms/details?${params.toString()}`, { signal: controller.signal })
            .then((res) => res.json())
            .then((payload) => {
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                if (!payload?.success || !payload?.data) {
                    setFormDetails(null);
                    setFormDetailsError(payload?.message || t("request_form.error_form_not_found", language === "tr" ? "Form bulunamadı." : "Form not found."));
                    return;
                }
                const details = payload.data as FormDetails;
                setFormDetails(details);
                setFormDetailsError(null);
                const nextAgreements: Record<number, boolean> = {};
                (details?.agreements?.required || []).forEach((a) => {
                    nextAgreements[a.id] = true;
                });
                (details?.agreements?.optional || []).forEach((a) => {
                    nextAgreements[a.id] = false;
                });
                setAgreementsState(nextAgreements);
            })
            .catch((err) => {
                if (err.name === "AbortError") return;
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                setFormDetails(null);
                setFormDetailsError(t("request_form.error_generic", language === "tr" ? "Form yüklenemedi." : "Form could not be loaded."));
            })
            .finally(() => {
                setFormDetailsLoading(false);
            });

        return () => {
            controller.abort();
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        };
    // t fonksiyonu her render'da yeni referans alabildiği için dependency'de kullanılmıyor; sonsuz döngüyü önler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, productType, languageId, initialFormDetails?.id]);

    const requiredAgreements = formDetails?.agreements?.required ?? [];
    const optionalAgreements = formDetails?.agreements?.optional ?? [];
    const allAgreementsAccepted = requiredAgreements.every((a) => agreementsState[a.id] === true);
    const fields = formDetails?.fields ?? [];
    const fieldValidation = useMemo(() => validateDynamicFormFields(fields, formValues), [fields, formValues]);
    const submitReadiness = useFormSubmitReadiness({
        formLoading: formDetailsLoading,
        captchaLoading: captchaConfigLoading,
    });
    const isFormValid =
        allAgreementsAccepted && fieldValidation.valid && submitReadiness.canSubmit && fields.length > 0;

    /** Title+Name+Surname yan yana, Departure+Return yan yana (sticky scroll’da submit’in görünmesi için) */
    const groupedFields = useMemo(() => {
        const out: Array<{ type: "row3" | "row2" | "single"; fields: FormField[]; startIndex: number }> = [];
        for (let i = 0; i < fields.length; i++) {
            const f = fields[i];
            const name = (f.name || f.field || `field_${i}`).toLowerCase();
            const nextName = fields[i + 1] ? (fields[i + 1].name || fields[i + 1].field || "").toLowerCase() : "";
            const next2Name = fields[i + 2] ? (fields[i + 2].name || fields[i + 2].field || "").toLowerCase() : "";
            if (name === "title" && nextName === "name" && next2Name === "surname") {
                out.push({ type: "row3", fields: [fields[i], fields[i + 1], fields[i + 2]], startIndex: i });
                i += 2;
            } else if (
                (name.includes("departure") || name.includes("travel") || name === "date") &&
                nextName.includes("return")
            ) {
                out.push({ type: "row2", fields: [fields[i], fields[i + 1]], startIndex: i });
                i += 1;
            } else {
                out.push({ type: "single", fields: [fields[i]], startIndex: i });
            }
        }
        return out;
    }, [fields]);
    const isCaptchaRequired = Boolean(captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider);

    const handleFieldChange = (name: string, value: string) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleAgreementChange = (id: number, checked: boolean) => {
        setAgreementsState((prev) => ({ ...prev, [id]: checked }));
    };

    // Captcha widget (v2 / Turnstile) – ContactForm ile aynı akış
    useEffect(() => {
        if (captchaConfigLoading || !captchaConfig?.is_enabled || !captchaConfig?.site_key || !captchaConfig?.provider) return;
        const provider = captchaConfig.provider;
        const siteKey = captchaConfig.site_key;
        const scriptUrl = captchaConfig.script_url;

        const initCaptcha = async () => {
            try {
                if (provider === "google_recaptcha_v2") {
                    await loadRecaptchaScript(scriptUrl || undefined);
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById("request-form-captcha-container");
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderRecaptchaWidget("request-form-captcha-container", siteKey, () => {}).then((id) => {
                                captchaWidgetIdRef.current = id;
                            }).catch(() => {});
                        }
                    }, 100);
                    setTimeout(() => clearInterval(checkContainer), 10000);
                } else if (provider === "google_recaptcha_v3" || provider === "google_recaptcha_enterprise") {
                    await loadRecaptchaV3Script(
                        siteKey,
                        provider === "google_recaptcha_enterprise" ? scriptUrl ?? undefined : undefined
                    );
                } else if (provider === "cloudflare_turnstile") {
                    await loadTurnstileScript(scriptUrl || undefined);
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById("request-form-captcha-container");
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderTurnstileWidget("request-form-captcha-container", siteKey, () => {}).then((id) => {
                                captchaWidgetIdRef.current = id;
                            }).catch(() => {});
                        }
                    }, 100);
                    setTimeout(() => clearInterval(checkContainer), 10000);
                }
            } catch {}
        };
        const t = setTimeout(initCaptcha, 500);
        return () => {
            clearTimeout(t);
            if (captchaWidgetIdRef.current != null && provider === "google_recaptcha_v2") {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaWidgetIdRef.current != null && provider === "cloudflare_turnstile") {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
                removeTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        };
    }, [captchaConfig, captchaConfigLoading]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formDetails || isSubmitting) return;
        const formEl = e.currentTarget;
        if (formEl instanceof HTMLFormElement && !formEl.reportValidity()) {
            return;
        }
        const validation = validateDynamicFormFields(fields, formValues);
        if (!validation.valid) {
            setSubmitError(
                t(
                    "request_form.validation_required",
                    language === "tr" ? "Lütfen zorunlu alanları doldurun." : "Please fill in all required fields.",
                ),
            );
            if (validation.firstInvalidKey) {
                const el = formEl.querySelector<HTMLElement>(`[name="${validation.firstInvalidKey}"]`);
                el?.focus();
            }
            return;
        }
        if (!allAgreementsAccepted) {
            setSubmitError(
                t(
                    "request_form.validation_agreements",
                    language === "tr" ? "Lütfen zorunlu sözleşmeleri onaylayın." : "Please accept the required agreements.",
                ),
            );
            return;
        }
        if (!submitReadiness.canSubmit) return;
        setSubmitError(null);

        // Checkout'a yönlendirme akışı: captcha gerekmez (checkout'ta alınacak), direkt redirect
        if (redirectToCheckout && onProceedToCheckout) {
            const agreementsPayload = [
                ...requiredAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: true })),
                ...optionalAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: false })),
            ];
            let rooms: Array<{ adults: number; children: number; childAges?: number[] }> = [];
            let guestCount = 1;
            let childCount = 0;
            const travelDate = formValues.departure_date ?? formValues.travel_date ?? formValues.check_in_date ?? "";
            const checkIn = formValues.check_in_date ?? travelDate;
            const checkOut = formValues.check_out_date ?? formValues.return_date;

            if (requestFormRooms.length > 0) {
                rooms = requestFormRooms.map((r) => ({
                    adults: r.adults,
                    children: r.children ?? 0,
                    childAges: r.childAges ?? [],
                }));
                guestCount = rooms.reduce((sum, r) => sum + r.adults, 0);
                childCount = rooms.reduce((sum, r) => sum + r.children, 0);
            } else {
                const p = parseInt(String(formValues.people ?? 1), 10);
                guestCount = Math.max(1, p);
                rooms = [{ adults: guestCount, children: 0, childAges: [] }];
            }

            const summary: RequestCheckoutSummary = {
                flow: "request_first",
                form_id: formDetails.id,
                product_id: productId,
                product_type: productType,
                product_name: productName,
                travel_date: travelDate || undefined,
                check_in_date: checkIn || undefined,
                check_out_date: checkOut || undefined,
                rooms: rooms.length > 0 ? rooms : undefined,
                guest_count: guestCount,
                child_count: childCount,
                form_values: { ...formValues },
                agreements: agreementsPayload,
                language_id: languageId,
            };
            onProceedToCheckout(summary);
            return;
        }

        let recaptchaToken = "";
        if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
            if (captchaConfig.provider === "google_recaptcha_v2") {
                recaptchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || "";
                if (!recaptchaToken) {
                    setSubmitError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
                    return;
                }
            } else if (captchaConfig.provider === "google_recaptcha_v3" || captchaConfig.provider === "google_recaptcha_enterprise") {
                try {
                    await loadRecaptchaV3Script(
                        captchaConfig.site_key,
                        captchaConfig.provider === "google_recaptcha_enterprise" ? captchaConfig.script_url ?? undefined : undefined
                    );
                    recaptchaToken = await getRecaptchaV3Token(captchaConfig.site_key, "request_form");
                } catch {
                    setSubmitError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
                    return;
                }
            } else if (captchaConfig.provider === "cloudflare_turnstile") {
                recaptchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || "";
                if (!recaptchaToken) {
                    setSubmitError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
                    return;
                }
            }
        }

        setIsSubmitting(true);

        const agreementsPayload = [
            ...requiredAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: true })),
            ...optionalAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: false })),
        ];

        const payload: Record<string, unknown> = {
            form_id: formDetails.id,
            product_id: productId,
            product_type: productType,
            ...formValues,
            newsletter_subscribed: newsletterSubscribed,
            agreements: agreementsPayload,
        };
        if (languageId != null) payload.language_id = languageId;
        if (requestFormRooms.length > 0) {
            const roomsPayload = requestFormRooms.map((r) => ({
                adults: r.adults,
                children: r.children ?? 0,
                childAges: r.childAges ?? [],
            }));
            payload.rooms = roomsPayload;
            const totalPeople = requestFormRooms.reduce((sum, r) => sum + r.adults + (r.children ?? 0), 0);
            payload.people = String(totalPeople);
            payload.guest_count = totalPeople;
        }
        if (recaptchaToken) payload.recaptcha_token = recaptchaToken;

        try {
            const res = await fetch("/api/form/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const errMsg =
                    data?.message ?? data?.error ?? (Array.isArray(data?.errors) ? Object.values(data.errors).flat().join(" ") : null) ?? t("request_form.error_generic", language === "tr" ? "Talebiniz gönderilemedi. Lütfen tekrar deneyin." : "Your request could not be sent. Please try again.");
                if (process.env.NODE_ENV === "development") {
                    console.warn("[RequestForm] Submit error:", res.status, data);
                }
                setSubmitError(errMsg);
                // reCAPTCHA v2 token tek kullanımlık; hata sonrası yeni token alınabilmesi için widget sıfırlanır
                if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                }
                return;
            }
            setSubmitSuccess(true);
            if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        } catch {
            setSubmitError(t("request_form.error_generic", language === "tr" ? "Talebiniz gönderilemedi. Lütfen tekrar deneyin." : "Your request could not be sent. Please try again."));
            // Ağ hatası sonrası reCAPTCHA widget sıfırla (tekrar denemede yeni token gerekir)
            if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (formDetailsLoading) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-alsero-first" />
                    <span className="ml-2 text-alsero-dark-gray text-sm">{t("request_form.loading", language === "tr" ? "Form yükleniyor..." : "Loading form...")}</span>
                </div>
            </div>
        );
    }

    if (formDetailsError || !formDetails) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                    <p className="text-amber-800 text-sm">
                        {formDetailsError ?? t("request_form.no_form_defined", language === "tr" ? "Talep formu şu an kullanılamıyor. Lütfen telefon veya iletişim formu ile bize ulaşın." : "The request form is temporarily unavailable. Please contact us by phone or via the contact form.")}
                    </p>
                </div>
            </div>
        );
    }

    if (submitSuccess) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="rounded-lg border border-green-200 bg-green-50 p-5 text-center">
                    <p className="text-green-800 font-medium">{t("request_form.success_message", language === "tr" ? "Talebiniz alındı. En kısa sürede size dönüş yapılacaktır." : "Your request has been received. We will get back to you as soon as possible.")}</p>
                </div>
            </div>
        );
    }

    const hasNoFields = (formDetails?.fields ?? []).length === 0;
    if (hasNoFields) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                    <p className="text-amber-800 text-sm">
                        {t("request_form.no_fields_defined", language === "tr" ? "Talep formu hazırlanıyor. Lütfen kısa süre sonra tekrar deneyin veya iletişim kanallarımızı kullanın." : "The request form is being prepared. Please try again shortly or use our contact channels.")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={`p-5 space-y-5 ${className}`} autoComplete="off" noValidate={false}>
            <div className="space-y-4">
            {groupedFields.map((item, groupIdx) => {
                const renderOne = (field: FormField, index: number, smallLabel?: boolean) => {
                const lblClass = smallLabel ? labelClassSmall : labelClass;
                const fieldName = field.name || field.field || `field_${index}`;
                const fieldType = (field.type || "text") as string;
                const fieldLabel = field.label || field.title || fieldName;
                const isRequired = field.required !== false && (field.required === true || field.required === "true" || field.required === 1);
                const value = formValues[fieldName] ?? "";

                const optionList: Array<{ value: string; label: string }> = Array.isArray(field.options)
                    ? field.options.map((o) => (typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label || o.value }))
                    : fieldName.toLowerCase() === "title"
                    ? [{ value: "", label: "" }, "Mr", "Mrs", "Ms", "Miss", "Dr", "Other"].map((x) => (typeof x === "string" ? { value: x, label: x } : x))
                    : [];

                // Country: form builder bazen type "select" gönderir; önce country kontrolü yap ki Title seçenekleri göstermesin
                if (fieldType === "country" || fieldName === "country_id" || (fieldName.toLowerCase().includes("country") && fieldName !== "country")) {
                    const countryOptions = [{ value: "", label: field.placeholder || t("request_form.country_placeholder", language === "tr" ? "Ülke seçiniz" : "Select country") }, ...countries.map((c) => ({ value: String(c.id), label: c.name }))];
                    const selectedCountry = value ? countryOptions.find((o) => o.value === value) ?? null : null;
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <label htmlFor={`request-form-${fieldName}`} className={lblClass}>
                                {fieldLabel}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <ReactSelect
                                inputId={`request-form-${fieldName}`}
                                value={selectedCountry}
                                onChange={(opt) => handleFieldChange(fieldName, (opt as { value: string } | null)?.value ?? "")}
                                options={countryOptions}
                                placeholder={field.placeholder || t("request_form.country_placeholder", language === "tr" ? "Ülke seçiniz" : "Select country")}
                                isClearable={!isRequired}
                                isSearchable
                                styles={searchableSelectStyles}
                                noOptionsMessage={() => t("request_form.no_options", language === "tr" ? "Seçenek yok" : "No options")}
                                loadingMessage={() => t("request_form.loading", language === "tr" ? "Yükleniyor..." : "Loading...")}
                                classNamePrefix="request-form-select"
                                components={{ Input: NoAutocompleteInput }}
                            />
                        </div>
                    );
                }

                if (fieldType === "select" || fieldName.toLowerCase() === "title") {
                    const defaultTitleOptions = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"].map((x) => ({ value: x, label: x }));
                    const fromField = optionList.length ? optionList : defaultTitleOptions;
                    const options = !isRequired ? [{ value: "", label: field.placeholder || t("request_form.select_placeholder", language === "tr" ? "Seçiniz" : "Select") }, ...fromField] : fromField;
                    const selectedOption = value ? options.find((o) => o.value === value) ?? null : null;
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <label htmlFor={`request-form-${fieldName}`} className={lblClass}>
                                {fieldLabel}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <ReactSelect
                                inputId={`request-form-${fieldName}`}
                                value={selectedOption}
                                onChange={(opt) => handleFieldChange(fieldName, (opt as { value: string } | null)?.value ?? "")}
                                options={options}
                                placeholder={field.placeholder || t("request_form.select_placeholder", language === "tr" ? "Seçiniz" : "Select")}
                                isClearable={!isRequired}
                                isSearchable
                                styles={searchableSelectStyles}
                                noOptionsMessage={() => t("request_form.no_options", language === "tr" ? "Seçenek yok" : "No options")}
                                classNamePrefix="request-form-select"
                            />
                        </div>
                    );
                }

                if (fieldType === "textarea" || fieldType === "textbox") {
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <label htmlFor={fieldName} className={lblClass}>
                                {fieldLabel}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <textarea
                                id={fieldName}
                                name={fieldName}
                                required={isRequired}
                                rows={field.rows ?? 4}
                                value={value}
                                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                placeholder={field.placeholder}
                                className={`${inputClass} resize-y min-h-[100px]`}
                            />
                        </div>
                    );
                }

                if (fieldType === "email" || fieldName.toLowerCase().includes("email")) {
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <label htmlFor={fieldName} className={lblClass}>
                                {fieldLabel}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <input
                                id={fieldName}
                                name={fieldName}
                                type="email"
                                required={isRequired}
                                value={value}
                                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                placeholder={field.placeholder}
                                className={inputClass}
                            />
                        </div>
                    );
                }

                if (fieldType === "number" || fieldName.toLowerCase().includes("people") || fieldName.toLowerCase().includes("guest")) {
                    const isPeopleField = fieldName.toLowerCase().includes("people") || fieldName.toLowerCase().includes("guest");
                    if (isPeopleField || fieldLabel?.toLowerCase().includes("guest")) {
                        const room = requestFormRooms[0] ?? { adults: 2, children: 0, childAges: [] };
                        const updateRoom = (updates: Partial<Room>) => {
                            setRequestFormRooms((prev) => {
                                const list = prev.length ? prev : [{ adults: 2, children: 0, childAges: [] }];
                                const current = list[0];
                                let newAdults = updates.adults ?? current.adults;
                                let newChildren = updates.children ?? current.children;
                                let newChildAges = updates.childAges ?? current.childAges ?? [];
                                if (updates.adults !== undefined) {
                                    newAdults = Math.max(1, Math.min(20, updates.adults));
                                }
                                if (updates.children !== undefined) {
                                    newChildren = Math.max(0, Math.min(20, updates.children));
                                    newChildAges = Array.from({ length: newChildren }, (_, i) => newChildAges[i] ?? 0);
                                }
                                const next = [{ ...current, adults: newAdults, children: newChildren, childAges: newChildAges }, ...list.slice(1)];
                                return next;
                            });
                            const newAdults = updates.adults !== undefined ? Math.max(1, Math.min(20, updates.adults)) : room.adults;
                            const newChildren = updates.children !== undefined ? Math.max(0, Math.min(20, updates.children)) : room.children;
                            handleFieldChange(fieldName, String(newAdults + newChildren));
                        };
                        return (
                            <div key={fieldName} className="space-y-1.5">
                                <label className={lblClass}>
                                    {t("reservation.guest_count", "Number of Guests")}
                                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                                            {getVariable("room_travelers_adults", "Adults")}
                                        </label>
                                        <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden bg-white">
                                            <button
                                                type="button"
                                                onClick={() => updateRoom({ adults: room.adults - 1 })}
                                                disabled={room.adults <= 1}
                                                aria-label="Decrease adults"
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            >
                                                −
                                            </button>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={String(room.adults)}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, "");
                                                    const v = digits ? parseInt(digits, 10) : 1;
                                                    updateRoom({ adults: Number.isNaN(v) ? 1 : Math.max(1, Math.min(20, v)) });
                                                }}
                                                className="flex-1 min-w-[2.5rem] py-2 text-center text-sm font-semibold text-gray-900 bg-transparent outline-none"
                                                aria-label="Adults count"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => updateRoom({ adults: room.adults + 1 })}
                                                disabled={room.adults >= 20}
                                                aria-label="Increase adults"
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-l border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                                            {getVariable("room_travelers_children", "Children")}
                                        </label>
                                        <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden bg-white">
                                            <button
                                                type="button"
                                                onClick={() => updateRoom({ children: room.children - 1 })}
                                                disabled={room.children <= 0}
                                                aria-label="Decrease children"
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            >
                                                −
                                            </button>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={String(room.children)}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, "");
                                                    const v = digits ? parseInt(digits, 10) : 0;
                                                    updateRoom({ children: Number.isNaN(v) ? 0 : Math.max(0, Math.min(20, v)) });
                                                }}
                                                className="flex-1 min-w-[2.5rem] py-2 text-center text-sm font-semibold text-gray-900 bg-transparent outline-none"
                                                aria-label="Children count"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => updateRoom({ children: room.children + 1 })}
                                                disabled={room.children >= 20}
                                                aria-label="Increase children"
                                                className="flex items-center justify-center w-10 h-10 text-alsero-first border-l border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <label htmlFor={fieldName} className={lblClass}>
                                {fieldLabel}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <input
                                id={fieldName}
                                name={fieldName}
                                type="number"
                                min={1}
                                max={999}
                                required={isRequired}
                                value={value}
                                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                placeholder={field.placeholder}
                                className={inputClass}
                            />
                        </div>
                    );
                }

                if (fieldType === "tel" || fieldType === "phone" || fieldName.toLowerCase().includes("phone") || fieldName.toLowerCase().includes("mobile")) {
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <label htmlFor={`request-form-${fieldName}`} className={lblClass}>
                                {fieldLabel}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <PhoneInput
                                name={fieldName}
                                defaultCountry="tr"
                                value={value}
                                onChange={(phoneValue) => handleFieldChange(fieldName, phoneValue)}
                                placeholder={field.placeholder || t("request_form.phone_placeholder", "Mobile Phone")}
                                className="border border-[rgba(0,0,0,0.08)] focus-within:border-[rgba(0,0,0,0.40)] transition-all duration-300 rounded-lg !text-[#4F545A] font-medium text-sm px-2 py-2"
                                inputClassName="!border-0 w-full"
                                countrySelectorStyleProps={{
                                    buttonClassName: "!border-0 !px-2 !rounded-lg transition-all duration-300 hover:bg-gray-50",
                                    dropdownStyleProps: {
                                        className: "!bg-white !border !border-[rgba(0,0,0,0.08)] !rounded-lg !shadow-lg !mt-1 !max-h-64 !overflow-y-auto",
                                        listItemClassName: "!px-3 !py-2 hover:!bg-alsero-soft-white !transition-colors !duration-200 !cursor-pointer !flex !items-center !gap-2",
                                        listItemFlagClassName: "!text-lg !mr-2",
                                        listItemCountryNameClassName: "!text-gray-700 !font-medium !text-sm",
                                        listItemDialCodeClassName: "!text-gray-500 !text-sm !ml-auto",
                                    },
                                }}
                            />
                        </div>
                    );
                }

                if (fieldName.toLowerCase().includes("date") || fieldName.toLowerCase().includes("departure") || fieldName.toLowerCase().includes("travel") || fieldName.toLowerCase().includes("return")) {
                    const dateValue = value ? new Date(value) : undefined;
                    const nameLower = fieldName.toLowerCase();
                    const isReturnField =
                        nameLower.includes("return") || nameLower.includes("check_out") || nameLower.includes("checkout");
                    const departureRaw =
                        formValues.departure_date ||
                        formValues.travel_date ||
                        formValues.check_in_date ||
                        "";
                    let minSelectable = startOfDay(new Date());
                    if (isReturnField && departureRaw) {
                        const parsed = parseISO(departureRaw);
                        if (!Number.isNaN(parsed.getTime())) {
                            minSelectable = startOfDay(parsed);
                        }
                    }
                    return (
                        <div key={fieldName} className="space-y-1.5">
                            <DatePickerCustom
                                label={fieldLabel}
                                labelClassName={lblClass}
                                selected={dateValue}
                                onSelect={(d) => handleFieldChange(fieldName, d ? format(d, "yyyy-MM-dd") : "")}
                                disabled={{ before: minSelectable }}
                                language={language}
                            />
                        </div>
                    );
                }

                return (
                    <div key={fieldName} className="space-y-1.5">
                        <label htmlFor={fieldName} className={lblClass}>
                            {fieldLabel}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            id={fieldName}
                            name={fieldName}
                            type={fieldType === "number" ? "number" : "text"}
                            required={isRequired}
                            value={value}
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            placeholder={field.placeholder}
                            className={inputClass}
                        />
                    </div>
                );
                };

                if (item.type === "row3") {
                    return (
                        <div key={`g-${groupIdx}`} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {item.fields.map((field, i) => (
                                <div key={field.name || field.field || i}>{renderOne(field, item.startIndex + i, true)}</div>
                            ))}
                        </div>
                    );
                }
                if (item.type === "row2") {
                    return (
                        <div key={`g-${groupIdx}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {item.fields.map((field, i) => (
                                <div key={field.name || field.field || i}>{renderOne(field, item.startIndex + i)}</div>
                            ))}
                        </div>
                    );
                }
                return <div key={`g-${groupIdx}`}>{renderOne(item.fields[0], item.startIndex)}</div>;
            })}
            </div>

            {(requiredAgreements.length > 0 || optionalAgreements.length > 0) && (
                <div className="space-y-3 pt-4 border-t border-[rgba(0,0,0,0.08)]">
                    <h4 className={labelClass}>{t("reservation.agreements", language === "tr" ? "Sözleşmeler" : "Agreements")}</h4>
                    {requiredAgreements.length > 0 && (
                        <div className="text-alsero-dark-gray text-sm">
                            {requiredAgreements.map((agreement, index) => (
                                <span key={agreement.id}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedAgreement(agreement);
                                            setAgreementModalOpen(true);
                                        }}
                                        className="text-alsero-first hover:underline font-semibold"
                                    >
                                        {agreement.title}
                                    </button>
                                    {index < requiredAgreements.length - 1 ? (
                                        <span className="text-alsero-dark-gray"> {t("request_form.agreement_separator", language === "tr" ? "buradan, " : "from here, ")} </span>
                                    ) : (
                                        <span className="text-alsero-dark-gray"> {t("request_form.agreement_suffix", language === "tr" ? "buradan " : "from here ")} </span>
                                    )}
                                </span>
                            ))}
                            {t("request_form.agreement_end", language === "tr" ? "ulaşabilirsiniz." : "you can access them here.")}
                        </div>
                    )}
                    {optionalAgreements.map((agreement) => (
                        <div key={agreement.id} className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                id={`agreement-opt-${agreement.id}`}
                                checked={agreementsState[agreement.id] ?? false}
                                onChange={(e) => handleAgreementChange(agreement.id, e.target.checked)}
                                className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-alsero-soft-white rounded cursor-pointer"
                            />
                            <label htmlFor={`agreement-opt-${agreement.id}`} className="flex-1 text-xs text-alsero-dark-gray cursor-pointer">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedAgreement(agreement);
                                        setAgreementModalOpen(true);
                                    }}
                                    className="text-alsero-first hover:underline font-semibold"
                                >
                                    {agreement.title}
                                </button>
                                {t("request_form.agreement_optional_confirm", language === "tr" ? "'ni okudum, onaylıyorum." : " – I have read and accept.")}
                            </label>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-start gap-2 pt-2">
                <input
                    type="checkbox"
                    id="request-form-newsletter"
                    checked={newsletterSubscribed}
                    onChange={(e) => setNewsletterSubscribed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-alsero-soft-white rounded cursor-pointer"
                    aria-describedby="request-form-newsletter-label"
                />
                <label id="request-form-newsletter-label" htmlFor="request-form-newsletter" className="flex-1 text-sm text-alsero-dark-gray cursor-pointer">
                    {t("form.newsletter_subscribe_label", "I would like to subscribe to the newsletter. You can check this to receive campaigns and news by email.")}
                </label>
            </div>

            {/* Captcha: entegrasyondan config, ContactForm ile aynı */}
            {captchaConfigLoading && (
                <div className="flex items-center gap-2 text-sm text-alsero-dark-gray">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-alsero-first border-t-transparent" />
                    {t("request_form.captcha_loading", language === "tr" ? "Doğrulama yükleniyor..." : "Verification loading...")}
                </div>
            )}
            {!captchaConfigLoading && captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider && (
                <>
                    {(captchaConfig.provider === "google_recaptcha_v2" || captchaConfig.provider === "cloudflare_turnstile") && (
                        <div id="request-form-captcha-container" className="min-h-[78px]" />
                    )}
                    {(captchaConfig.provider === "google_recaptcha_v3" || captchaConfig.provider === "google_recaptcha_enterprise") && (
                        <div className="space-y-1.5">
                            <p className="text-sm text-alsero-dark-gray/80">{t("common.captcha_auto_note", language === "tr" ? "Robot doğrulaması gönder sırasında otomatik yapılır." : "Verification is done automatically when you submit.")}</p>
                            <RecaptchaLegalNotice language={language} />
                        </div>
                    )}
                </>
            )}

            {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-red-600 text-sm">{submitError}</p>
                </div>
            )}

            <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
                    isFormValid && !isSubmitting && !captchaConfigLoading ? "bg-alsero-first hover:bg-alsero-first/90 focus:outline-none focus:ring-2 focus:ring-alsero-first/20" : "bg-gray-400 cursor-not-allowed"
                }`}
            >
                {isSubmitting ? t("request_form.submitting", language === "tr" ? "Gönderiliyor..." : "Sending...") : redirectToCheckout
                    ? t("request_form.proceed_to_checkout", language === "tr" ? "Devam Et" : "Proceed")
                    : t("request_form.submit", language === "tr" ? "Gönder" : "Submit")}
            </button>

            {selectedAgreement && (
                <AgreementModal
                    isOpen={agreementModalOpen}
                    onClose={() => {
                        setAgreementModalOpen(false);
                        setSelectedAgreement(null);
                    }}
                    agreementSlug={selectedAgreement.slug}
                    language={language}
                    agreementId={selectedAgreement.id}
                    isRequired={selectedAgreement.is_required}
                    onAccept={(agreementId) => {
                        setAgreementsState((prev) => ({ ...prev, [agreementId]: true }));
                    }}
                />
            )}
        </form>
    );
}
